/* ==========================================================================
   lsc-testimonials-modern.js
   --------------------------------------------------------------------------
   LSC - 2026-10-12 home audit: behaviour for
   sections/lsc-testimonials-modern.liquid ("Testimonials (Modern)"), moved
   out of the section's inline <script> (~22KB re-sent in the HTML of every
   page view) into this cached asset, loaded with `defer`.

   The logic is the owner's 2026-10-12 version, ported unchanged in
   behaviour (see project doc lsc-testimonials-autoplay-pause-control.md):
     - ONE pause/play toggle per instance, aria-pressed carries the state,
       accessible name stays "Pause autoplay";
     - syncAutoplay() is the ONLY place the scroll layout's animation-frame
       loop is scheduled/cancelled and the only place the "--autoplaying"
       class is set; it re-derives the answer from every condition;
     - six separate stop reasons: userPaused (sticky), hoverPaused (pointer
       over the TRACK, not the section), dragPaused (drag/touch/arrow-or-dot
       cooldown), startupHeld (read-time delay), offScreen, and focus inside
       the track - which is READ from document.activeElement, never stored;
     - lastFrameTime is dropped on every (re)start and deltas are clamped;
     - measurements are cached and only invalidated (resize, ResizeObserver,
       window load), never recomputed per frame;
     - arrow handlers select ".lsc-tm__arrow[data-dir]" so the toggle (which
       shares .lsc-tm__arrow for its styling) is never wired as a chevron.

   Changes made while moving it (each marked "LSC - 2026-10-12 home audit"):
     1. Runs its global setup once per page however many sections include
        the <script> tag, and registers the theme editor's
        shopify:section:load / shopify:section:unload listeners exactly once.
        The inline version added ANOTHER document-level section:load listener
        every time it ran (i.e. on every editor re-render).
     2. Every instance's listeners hang off one AbortController, and its
        observers / timers / animation frame are in a cleanup list, so
        section:unload tears an instance down completely. Before, a
        re-rendered section left its window resize/load listeners,
        ResizeObserver and IntersectionObservers attached to dead markup.
     3. Scroll layout dots: plain buttons with aria-current (the markup is a
        labelled role="group" now, not a fake tablist); the active dot also
        follows the autoplay drift (the debounced scroll handler is now a
        trailing throttle - see onTrackScroll); and a dot click goes to the
        card at the dot's own POSITION, which stays right after randomize
        (data-goto holds the original block index).
     4. Scroll layout touch handling: touchcancel releases the hold like
        touchend, and the pointercancel a browser fires when it takes over a
        touch pan no longer releases the hold mid-swipe; the arrow/dot
        cooldown timer no longer releases a drag/touch that is still held.

   Per-instance config comes from data-* attributes on <lsc-testimonials>
   (printed by the section):
     data-layout         spotlight | scroll | grid | marquee
     data-autoplay       "true" / "false"
     data-speed          ms - spotlight dwell / scroll: ms to drift one card
     data-initial-delay  ms - scroll layout read-time before drifting starts
     data-randomize      "true" / "false"
   ========================================================================== */
(function () {
  'use strict';

  // LSC - 2026-10-12 home audit: every instance of the section carries a
  // <script defer> for this file, so it can execute several times on one
  // page. Later runs only initialise instances that are not initialised yet
  // (init() is idempotent); nothing global is registered twice.
  if (window.LscTestimonialsModern) {
    window.LscTestimonialsModern.initAll();
    return;
  }

  // Property on each <lsc-testimonials> root holding its teardown state.
  var INSTANCE_KEY = '__lscTm';

  function toArray(list) { return Array.prototype.slice.call(list); }

  // Fisher-Yates shuffle, in place.
  function shuffle(arr) {
    for (var idx = arr.length - 1; idx > 0; idx--) {
      var j = Math.floor(Math.random() * (idx + 1));
      var t = arr[idx]; arr[idx] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // AbortController with a no-op stand-in for very old browsers (there the
  // listeners simply cannot be removed, which only matters in the editor).
  function makeController() {
    if (window.AbortController) return new AbortController();
    return { signal: undefined, abort: function () {} };
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------------
     Randomize ("Randomize testimonial order"): shuffle once on init. Cards
     and their matching dots / filmstrip chips are shuffled with the SAME
     order so position N of one list still belongs to position N of the other.
     ------------------------------------------------------------------------ */
  function randomizeLayout(root) {
    if (root.dataset.randomize !== 'true') return;
    var layout = root.dataset.layout;
    var order;

    if (layout === 'grid') {
      var grid = root.querySelector('.lsc-tm__grid');
      if (grid) {
        shuffle(toArray(grid.children)).forEach(function (c) { grid.appendChild(c); });
      }
    } else if (layout === 'marquee') {
      // Two identical halves (the seamless loop); shuffle both the same way.
      var track = root.querySelector('.lsc-tm__track');
      if (track) {
        var half = track.children.length / 2;
        var originals = toArray(track.children);
        order = shuffle(originals.slice(0, half).map(function (_, idx) { return idx; }));
        order.forEach(function (origIdx) { track.appendChild(originals[origIdx]); });
        order.forEach(function (origIdx) { track.appendChild(originals[origIdx + half]); });
      }
    } else if (layout === 'scroll') {
      // Shuffle the scroll cards and their matching dots together.
      var scrollTrack = root.querySelector('.lsc-tm__scroll-track');
      var dotsWrap = root.querySelector('.lsc-tm__scroll-dots');
      if (scrollTrack) {
        var cardEls = toArray(scrollTrack.children);
        order = shuffle(cardEls.map(function (_, idx) { return idx; }));
        order.forEach(function (origIdx) { scrollTrack.appendChild(cardEls[origIdx]); });
        if (dotsWrap) {
          var dotEls = toArray(dotsWrap.children);
          order.forEach(function (origIdx) { dotsWrap.appendChild(dotEls[origIdx]); });
        }
      }
    } else {
      // Spotlight: shuffle slides and their filmstrip chips together, and
      // make the new first slide the active one.
      var slidesWrap = root.querySelector('.lsc-tm__slides');
      var filmstrip = root.querySelector('.lsc-tm__filmstrip');
      if (slidesWrap) {
        var slideEls = toArray(slidesWrap.children);
        order = shuffle(slideEls.map(function (_, idx) { return idx; }));
        order.forEach(function (origIdx, newIdx) {
          var sl = slideEls[origIdx];
          if (newIdx === 0) { sl.setAttribute('data-active', ''); } else { sl.removeAttribute('data-active'); }
          slidesWrap.appendChild(sl);
        });
        if (filmstrip) {
          var chipEls = toArray(filmstrip.children);
          order.forEach(function (origIdx, newIdx) {
            var c = chipEls[origIdx];
            c.setAttribute('aria-selected', newIdx === 0 ? 'true' : 'false');
            filmstrip.appendChild(c);
          });
        }
      }
    }
  }

  /* ------------------------------------------------------------------------
     The pause/play toggle, shared by all three moving layouts (WCAG 2.2.2).
     aria-pressed on the button is the single source of truth: this helper
     owns that attribute, the stylesheet reads it to choose the icon, and
     assistive tech reads it for the state. The accessible name ("Pause
     autoplay") never changes. What pausing MEANS is the caller's business
     (rAF drift, setInterval, or CSS animation), passed in as onToggle.
     Returns a no-op shaped object when the button is absent.
     ------------------------------------------------------------------------ */
  function wireTmPlayToggle(root, onToggle, on) {
    var btn = root.querySelector('[data-lsc-tm-playtoggle]');
    if (!btn) return { pressed: function () { return false; } };
    var pressed = false;
    on(btn, 'click', function () {
      pressed = !pressed;
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      onToggle(pressed);
    });
    return { pressed: function () { return pressed; } };
  }

  /* ------------------------------------------------------------------------
     "scroll" layout: native touch/trackpad scrolling comes free from
     overflow-x + scroll-snap in the CSS; this adds mouse drag, chevron
     paging, dot navigation + active state, and the autoplay drift.
     `on` adds a listener tied to this instance's AbortController;
     `cleanup` registers a teardown function.
     ------------------------------------------------------------------------ */
  function initScrollCarousel(root, on, cleanup) {
    var track = root.querySelector('.lsc-tm__scroll-track');
    if (!track) return;
    var cards = toArray(track.querySelectorAll('.lsc-tm__card'));
    var dots = toArray(root.querySelectorAll('.lsc-tm__scroll-dot'));
    if (!cards.length) return;

    // Force the carousel to start at the first card. The browser can scroll
    // this track on its own after init (e.g. bringing a fragment target into
    // view scrolls every ancestor scroller, possibly smoothly). An explicit
    // 'instant' scrollTo beats an in-progress smooth scroll; re-asserted on
    // the next frame and after window load so we win last.
    function resetToStart() {
      track.scrollTo({ left: 0, behavior: 'instant' });
    }
    resetToStart();
    var resetFrame = window.requestAnimationFrame(resetToStart);
    cleanup(function () { window.cancelAnimationFrame(resetFrame); });
    on(window, 'load', resetToStart, { once: true });

    // Measurements are CACHED, not taken per frame (a getComputedStyle +
    // getBoundingClientRect per animation frame was a forced style recalc and
    // layout sixty times a second). A zero width is never cached, so a track
    // that is not laid out yet simply measures again on the next call.
    var measured = false;
    var measuredStep = 0;
    var measuredMax = 0;

    function measure() {
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || 0) || 0;
      var width = cards[0].getBoundingClientRect().width;
      measuredStep = width + gap;
      measuredMax = track.scrollWidth - track.clientWidth;
      measured = width > 0;
    }

    function invalidateMeasurements() { measured = false; }
    function cardStep() { if (!measured) { measure(); } return measuredStep; }
    function trackMax() { if (!measured) { measure(); } return measuredMax; }

    // Anything that can change a card's width only INVALIDATES the cache;
    // the next cardStep() call does the work.
    var resizeTimer = null;
    on(window, 'resize', function () {
      invalidateMeasurements();
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(syncArrows, 120);
    });
    cleanup(function () { window.clearTimeout(resizeTimer); });
    on(window, 'load', invalidateMeasurements, { once: true });
    if (window.ResizeObserver) {
      var ro = new window.ResizeObserver(invalidateMeasurements);
      ro.observe(track);
      cleanup(function () { ro.disconnect(); });
    }

    // Index of the card whose left edge is nearest the current scroll
    // position (cards are in DOM order, i.e. AFTER any randomize).
    function nearestIndex() {
      var pos = track.scrollLeft;
      var best = 0, bestDist = Infinity;
      cards.forEach(function (c, idx) {
        var dist = Math.abs(c.offsetLeft - pos);
        if (dist < bestDist) { bestDist = dist; best = idx; }
      });
      return best;
    }

    // LSC - 2026-10-12 home audit: active dot = aria-current="true" (was
    // aria-selected on a fake tab). Dots and cards share positions (they are
    // shuffled together), so the dot at position N belongs to card N.
    function syncDots() {
      if (!dots.length) return;
      var active = nearestIndex();
      dots.forEach(function (d, idx) {
        if (idx === active) { d.setAttribute('aria-current', 'true'); } else { d.removeAttribute('aria-current'); }
      });
    }

    function syncArrows() {
      var prevBtn = root.querySelector('.lsc-tm__arrow[data-dir="prev"]');
      var nextBtn = root.querySelector('.lsc-tm__arrow[data-dir="next"]');
      var max = track.scrollWidth - track.clientWidth - 2;
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max;
    }

    // Chevrons page by one card width. '[data-dir]' excludes the pause/play
    // toggle, which shares .lsc-tm__arrow for its styling but has no direction.
    toArray(root.querySelectorAll('.lsc-tm__arrow[data-dir]')).forEach(function (b) {
      on(b, 'click', function () {
        var dir = b.dataset.dir === 'next' ? 1 : -1;
        track.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
      });
    });

    // LSC - 2026-10-12 home audit: a dot jumps to the card at the dot's own
    // POSITION. It used data-goto, which holds the ORIGINAL block index - after
    // "Randomize" shuffled cards and dots together, cards[data-goto] was some
    // other review, so the dot labelled "Review from X" scrolled to Y.
    dots.forEach(function (d, idx) {
      on(d, 'click', function () {
        var target = cards[idx];
        if (target) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });

    // Mouse click-and-drag scrolling (touch/trackpad already scroll natively).
    var isDown = false, startX = 0, startScroll = 0, moved = false;
    on(track, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true; moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('lsc-tm__scroll-track--dragging');
    });
    on(track, 'pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      track.scrollLeft = startScroll - dx;
    });
    function endDrag() {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('lsc-tm__scroll-track--dragging');
    }
    on(track, 'pointerup', endDrag);
    on(track, 'pointerleave', endDrag);
    on(track, 'pointercancel', endDrag);
    // Suppress the click-through on a card link/button right after a drag.
    on(track, 'click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);

    // LSC - 2026-10-12 home audit: TRAILING THROTTLE instead of a debounce.
    // The old handler cleared and re-set an 80ms timer on every scroll event.
    // The autoplay drift scrolls the track on every animation frame (~16ms
    // apart), so that timer was reset forever and never fired - the active dot
    // stayed stuck on the first card for as long as the strip drifted. Now an
    // event only schedules a sync if none is pending; the pending one reads
    // the position when it fires, and any event after it schedules the next,
    // so the final resting position is always picked up.
    var scrollSyncTimer = null;
    function onTrackScroll() {
      if (scrollSyncTimer !== null) return;
      scrollSyncTimer = window.setTimeout(function () {
        scrollSyncTimer = null;
        syncDots();
        syncArrows();
      }, 80);
    }
    on(track, 'scroll', onTrackScroll, { passive: true });
    cleanup(function () { window.clearTimeout(scrollSyncTimer); });

    // Autoplay drift: nudges scrollLeft on requestAnimationFrame rather than
    // paging on a timer, so it reads as "the strip is moving" and the visitor
    // can grab it mid-drift. Loops back to the start at the end.
    var autoplayEnabled = root.dataset.autoplay === 'true' && !prefersReducedMotion() && cards.length > 1;
    // "Speed" (ms) = seconds to drift past one card's width, so all layouts
    // feel similarly paced at the same setting.
    var speed = parseInt(root.dataset.speed, 10) || 5000;

    // Six separate reasons the drift can be stopped (see the project doc):
    //   userPaused  - the visitor pressed Pause. STICKY - the mouseleave that
    //                 follows the click must not clear it.
    //   hoverPaused - the pointer is over the TRACK (not the section, or the
    //                 pointer resting on Play would block Play).
    //   dragPaused  - a drag or touch is in progress, or an arrow/dot click is
    //                 in its cooldown.
    //   startupHeld - the read-time delay has not elapsed.
    //   offScreen   - the track is outside the viewport.
    //   + focus inside the TRACK, asked of document.activeElement at sync time
    //     and never stored (a stored focusin flag once stuck on forever).
    var userPaused = false;
    var hoverPaused = false;
    var dragPaused = false;
    var startupHeld = true;
    var offScreen = true;
    var rafId = null;
    var lastFrameTime = null;
    var cooldownTimer = null;
    // LSC - 2026-10-12 home audit: true between touchstart and
    // touchend/touchcancel. Only an INPUT to dragPaused, not a seventh
    // reason - it decides whether a pointer release may lift the hold.
    var touchActive = false;
    // track.scrollLeft reads back rounded; this unrounded accumulator is the
    // real drift position and the DOM is only written to.
    var scrollPos = track.scrollLeft;

    function focusInTrack() {
      return !!(document.activeElement && track.contains(document.activeElement));
    }

    function shouldDrift() {
      return autoplayEnabled && !userPaused && !hoverPaused && !dragPaused &&
             !startupHeld && !offScreen && !focusInTrack();
    }

    // The ONE place the loop is started or stopped, and the only place the
    // "--autoplaying" class is set. Re-derives the answer from all six
    // conditions every time, so a missed event costs one stale frame, never a
    // dead carousel. lastFrameTime is cleared on each start so a loop resumed
    // after a minute does not see a sixty-second delta and teleport.
    function syncAutoplay() {
      if (shouldDrift()) {
        track.classList.add('lsc-tm__scroll-track--autoplaying');
        if (rafId === null) {
          lastFrameTime = null;
          rafId = window.requestAnimationFrame(autoplayLoop);
        }
      } else {
        track.classList.remove('lsc-tm__scroll-track--autoplaying');
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }
      }
    }
    // Teardown: stop the loop and any pending cooldown.
    cleanup(function () {
      if (rafId !== null) { window.cancelAnimationFrame(rafId); rafId = null; }
      window.clearTimeout(cooldownTimer);
    });

    // Small state setters so every handler is one line and ends the same way.
    function holdForDrag() { dragPaused = true; syncAutoplay(); }
    function releaseDrag() { dragPaused = false; syncAutoplay(); }

    // LSC - 2026-10-12 home audit: pointer releases ignored while a TOUCH is
    // still down. On a touch swipe the browser fires pointercancel (then
    // pointerleave) the moment it takes the gesture over as a native pan;
    // those used to call releaseDrag, so the drift restarted under the
    // visitor's finger mid-swipe. touchend/touchcancel release it instead. In a
    // browser that sends no touch events touchActive never becomes true, so
    // the pointer events still release as before and nothing can stick.
    function releasePointer() { if (!touchActive) releaseDrag(); }
    function touchStart() { touchActive = true; holdForDrag(); }
    function touchEnd() { touchActive = false; releaseDrag(); }

    // Arrow/dot cooldown. The existing clearTimeout before setTimeout already
    // stopped repeated clicks stacking timers.
    // LSC - 2026-10-12 home audit: when the cooldown expires it no longer
    // releases a drag or touch that started meanwhile (tap a dot, then swipe
    // within `speed` ms: the timer used to restart the drift mid-swipe); that
    // gesture's own end event releases the hold.
    function holdCooldown() {
      dragPaused = true;
      window.clearTimeout(cooldownTimer);
      cooldownTimer = window.setTimeout(function () {
        cooldownTimer = null;
        if (!isDown && !touchActive) releaseDrag();
      }, speed);
      syncAutoplay();
    }

    function autoplayLoop(timestamp) {
      // Claim the frame as spent before anything can return early, so
      // syncAutoplay never tries to cancel an id that has already fired.
      rafId = null;
      if (!shouldDrift()) {
        track.classList.remove('lsc-tm__scroll-track--autoplaying');
        return;
      }

      if (lastFrameTime === null) { lastFrameTime = timestamp; }
      var deltaSeconds = (timestamp - lastFrameTime) / 1000;
      lastFrameTime = timestamp;
      // A backgrounded tab's first frame back can be seconds later: advance
      // by one frame's worth, not by the whole absence.
      if (deltaSeconds > 0.05) { deltaSeconds = 0.05; }

      var max = trackMax();
      if (max > 0) {
        // If the visitor (or an arrow/dot click) moved the track since the
        // last frame, resync so the drift continues from where they left it.
        if (Math.abs(track.scrollLeft - scrollPos) > 1.5) {
          scrollPos = track.scrollLeft;
        }
        scrollPos += (cardStep() / (speed / 1000)) * deltaSeconds;
        if (scrollPos >= max) { scrollPos = 0; }
        // NOT a plain scrollLeft assignment: that would honour the track's
        // CSS scroll-behavior:smooth, and sixty overlapping smooth scrolls a
        // second coalesce into no visible movement. 'instant' bypasses it.
        track.scrollTo({ left: scrollPos, behavior: 'instant' });
      }

      rafId = window.requestAnimationFrame(autoplayLoop);
    }

    if (autoplayEnabled) {
      // The pause/play button only sets the sticky flag; syncAutoplay works
      // out the rest (and drops "--autoplaying", restoring scroll-snap, so a
      // paused strip settles onto a card).
      wireTmPlayToggle(root, function (pressed) {
        userPaused = pressed;
        syncAutoplay();
      }, on);

      // Hover is watched on the TRACK (see the six reasons above).
      on(track, 'mouseenter', function () { hoverPaused = true; syncAutoplay(); });
      on(track, 'mouseleave', function () { hoverPaused = false; syncAutoplay(); });
      on(track, 'pointerdown', holdForDrag);
      on(track, 'pointerup', releasePointer);
      on(track, 'pointercancel', releasePointer);
      on(track, 'pointerleave', releasePointer);
      on(track, 'touchstart', touchStart, { passive: true });
      on(track, 'touchend', touchEnd, { passive: true });
      // LSC - 2026-10-12 home audit: touchcancel (the OS interrupting a
      // touch - an incoming call, a system gesture) ends the touch exactly
      // like touchend. Without it the hold stayed on until the next touch.
      on(track, 'touchcancel', touchEnd, { passive: true });

      // Focus is read, not stored, so these only prod a re-check. focusout
      // fires BEFORE the new element takes focus, so that check is deferred
      // a tick (read immediately it would see <body> mid-move).
      on(root, 'focusin', syncAutoplay);
      on(root, 'focusout', function () { window.setTimeout(syncAutoplay, 0); });

      // Arrow and dot clicks are manual navigation: hold for one `speed`
      // interval so their smooth scroll is not overridden by the next frame.
      toArray(root.querySelectorAll('.lsc-tm__arrow[data-dir]')).forEach(function (b) {
        on(b, 'click', holdCooldown);
      });
      dots.forEach(function (d) { on(d, 'click', holdCooldown); });

      // Read-time delay before the first drift, measured from when the
      // visitor actually SEES the cards (observer on the TRACK, threshold
      // 0.5), not from page load. Length from data-initial-delay (ms),
      // 2000ms for a section saved before that setting existed.
      var initialDelayMs = parseInt(root.dataset.initialDelay, 10);
      if (isNaN(initialDelayMs)) { initialDelayMs = 2000; }
      var initialDelayStarted = false;
      var initialDelayTimer = null;
      function startInitialDelay() {
        if (initialDelayStarted) return;
        initialDelayStarted = true;
        initialDelayTimer = window.setTimeout(function () { startupHeld = false; syncAutoplay(); }, initialDelayMs);
      }
      cleanup(function () { window.clearTimeout(initialDelayTimer); });

      if (window.IntersectionObserver) {
        // Observer one: the read-time delay; disconnects once it has fired.
        var delayObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              startInitialDelay();
              delayObserver.disconnect();
            }
          });
        }, { threshold: 0.5 });
        delayObserver.observe(track);
        cleanup(function () { delayObserver.disconnect(); });

        // Observer two: the off-screen gate, connected for the instance's
        // life. Separate observers (not one with [0, 0.5]) because only
        // isIntersecting has proven reliable here - intersectionRatio has
        // reported 0 for visible elements in this project before.
        var viewObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            offScreen = !entry.isIntersecting;
            syncAutoplay();
          });
        }, { threshold: 0 });
        viewObserver.observe(track);
        cleanup(function () { viewObserver.disconnect(); });
      } else {
        // No IntersectionObserver: assume visible and fall back to a
        // load-relative delay rather than never starting.
        offScreen = false;
        startInitialDelay();
      }

      // Nothing starts here (startupHeld/offScreen still set): this only
      // settles the class until the observers say otherwise.
      syncAutoplay();
    }

    syncDots();
    syncArrows();
  }

  /* ------------------------------------------------------------------------
     Marquee: the motion is a CSS animation, so pausing is a class
     (.lsc-tm__track--paused -> animation-play-state: paused) and a paused
     marquee keeps its position.
     ------------------------------------------------------------------------ */
  function initMarquee(root, on) {
    var mqTrack = root.querySelector('.lsc-tm__track');
    if (!mqTrack) return;
    wireTmPlayToggle(root, function (pressed) {
      mqTrack.classList.toggle('lsc-tm__track--paused', pressed);
    }, on);
  }

  /* ------------------------------------------------------------------------
     Spotlight: one slide at a time on a setInterval.
     ------------------------------------------------------------------------ */
  function initSpotlight(root, on, cleanup) {
    var slides = toArray(root.querySelectorAll('.lsc-tm__slide'));
    var chips = toArray(root.querySelectorAll('.lsc-tm__chip'));
    if (slides.length < 2) return;
    var i = 0, timer = null;
    // Sticky pause: every resume path goes through start(), which refuses
    // while this is set, so the button outlasts mouseleave/focusout.
    var userPaused = false;
    var autoplay = root.dataset.autoplay === 'true' && !prefersReducedMotion();
    var speed = parseInt(root.dataset.speed, 10) || 5000;

    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (sl, idx) {
        if (idx === i) { sl.setAttribute('data-active', ''); } else { sl.removeAttribute('data-active'); }
      });
      chips.forEach(function (c, idx) { c.setAttribute('aria-selected', idx === i ? 'true' : 'false'); });
    }
    function next() { show(i + 1); }
    function prev() { show(i - 1); }
    // stop() before every setInterval, so timers never stack.
    function start() { if (autoplay && !userPaused) { stop(); timer = setInterval(next, speed); } }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    cleanup(stop);

    chips.forEach(function (c) {
      on(c, 'click', function () { show(chips.indexOf(c)); start(); });
    });
    // '[data-dir]' excludes the pause/play toggle (one press would otherwise
    // both pause and step backwards).
    toArray(root.querySelectorAll('.lsc-tm__arrow[data-dir]')).forEach(function (b) {
      on(b, 'click', function () { if (b.dataset.dir === 'next') { next(); } else { prev(); } start(); });
    });
    // Pause stops the timer, Play starts it. Chevrons/chips still move the
    // slide while paused (start() is then a no-op, so it stays paused).
    wireTmPlayToggle(root, function (pressed) {
      userPaused = pressed;
      if (pressed) { stop(); } else { start(); }
    }, on);
    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', start);
    on(root, 'focusin', stop);
    on(root, 'focusout', start);

    // Theme editor: show the block being edited.
    on(root, 'shopify:block:select', function (e) {
      var idx = slides.indexOf(e.target.closest('.lsc-tm__slide'));
      if (idx > -1) { show(idx); stop(); }
    });

    show(0);
    start();
  }

  /* ------------------------------------------------------------------------
     Instance lifecycle.
     ------------------------------------------------------------------------ */
  function init(root) {
    if (!root || root[INSTANCE_KEY]) return;
    var controller = makeController();
    var cleanups = [];
    root[INSTANCE_KEY] = { controller: controller, cleanups: cleanups };
    // Kept for anything that inspected the old inline script's marker.
    root.dataset.tmInit = '1';

    // addEventListener tied to this instance's AbortController.
    function on(target, type, handler, options) {
      var opts = { signal: controller.signal };
      if (options === true) { opts.capture = true; }
      else if (options) { for (var k in options) { if (Object.prototype.hasOwnProperty.call(options, k)) opts[k] = options[k]; } }
      target.addEventListener(type, handler, opts);
    }
    function cleanup(fn) { cleanups.push(fn); }

    randomizeLayout(root);
    var layout = root.dataset.layout;
    if (layout === 'scroll') { initScrollCarousel(root, on, cleanup); }
    else if (layout === 'marquee') { initMarquee(root, on); }
    else if (layout === 'spotlight') { initSpotlight(root, on, cleanup); }
    // grid: randomize only, nothing else to wire.
  }

  function destroy(root) {
    var inst = root && root[INSTANCE_KEY];
    if (!inst) return;
    inst.controller.abort();
    inst.cleanups.forEach(function (fn) { try { fn(); } catch (err) { /* keep tearing down */ } });
    root[INSTANCE_KEY] = null;
    delete root.dataset.tmInit;
  }

  function initAll() {
    toArray(document.querySelectorAll('lsc-testimonials')).forEach(init);
  }

  // LSC - 2026-10-12 home audit: registered ONCE per page (guard at the
  // top). The editor fires section:unload for the old markup and
  // section:load for the re-rendered markup; the old element is torn down,
  // the new one (a fresh element, so not yet initialised) is set up.
  document.addEventListener('shopify:section:load', function (e) {
    toArray(e.target.querySelectorAll('lsc-testimonials')).forEach(init);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    toArray(e.target.querySelectorAll('lsc-testimonials')).forEach(destroy);
  });

  window.LscTestimonialsModern = { init: init, destroy: destroy, initAll: initAll };

  if (document.readyState !== 'loading') { initAll(); }
  else { document.addEventListener('DOMContentLoaded', initAll); }
})();
